import differenceInDays from 'date-fns/differenceInDays'
import format from 'date-fns/format'
import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'

import { Types } from 'service-api'
import { getInvoiceDates } from './invoices'
import { i18WithDefault as t } from './locales'
import { joinPetNamesFromProfile } from './pets'

export const isOrderConfirmed = (order?: Types.OrderClientDto | Types.OrderAdminDto) =>
  order?.orderStatus === 'CONFIRMED' || order?.orderStatus === 'PARTIALLY_CANCELED'

export const getOrderDates = (
  order: Types.OrderClientDto | Types.OrderAdminDto
): { minDate: Date | null; maxDate: Date | null } => {
  let minDate: Date | null = null,
    maxDate: Date | null = null
  order.invoices?.forEach((invoice) => {
    const { startDate, endDate } = getInvoiceDates(invoice)
    if (!minDate || isBefore(startDate, minDate)) {
      minDate = startDate
    }
    if (!maxDate || isAfter(endDate, maxDate)) {
      maxDate = endDate
    }
  })
  return { minDate, maxDate }
}

export const getServiceDatesText = (order: Types.OrderClientDto, options = {}) => {
  const mergedOptions = Object.assign({}, { fullYear: false, fullPeriod: false, fullYearLast: false }, options)

  const { minDate, maxDate } = getOrderDates(order)
  if (!minDate || !maxDate) {
    return t('global.label.N/A')
  }
  if (order.primaryServiceType?.serviceType?.name === 'daycare') {
    return order.invoices?.length === 1 ? format(minDate, 'MMM d, yyyy') : t('global.label.many')
  }

  if (differenceInDays(maxDate, minDate) <= 1 && !mergedOptions.fullPeriod) {
    return format(minDate, 'MMM d, yyyy')
  } else {
    return `${format(minDate, `MMM d${mergedOptions.fullYear ? ' yyyy' : ''}`)} - ${format(
      maxDate,
      `MMM d${mergedOptions.fullYear || mergedOptions.fullYearLast ? ' yyyy' : ''}`
    )}`
  }
}

export const getReservationSummary = (invoice: Types.CustomInvoiceAdminDto) => {
  return (invoice.items || []).map((item) => `${item.qty} ${item.displayName}`).join(', ')
}

export const orderPetProfiles = (order?: Types.OrderAdminDto) => {
  if (!order) {
    return []
  }
  return (
    order.pets
      ?.map((p) =>
        p.locationPetProfile
          ? p.locationPetProfile
          : ({ displayName: p.displayName } as Types.LocationPetProfileAdminDto)
      )
      ?.filter(Boolean) || []
  )
}

export const orderFormatPetNames = (order?: Types.OrderAdminDto) => {
  return joinPetNamesFromProfile(orderPetProfiles(order))
}

export const orderPetNamesOwner = (order?: Types.OrderAdminDto, petsToExclude?: string[]) => {
  if (!order) {
    return []
  }
  const pets = orderPetProfiles(order)
  return pets.reduce((acc, pet) => {
    if (petsToExclude?.includes(pet.id || '')) {
      return acc
    }
    const lastName = order.locationUserProfile?.lastName || ''
    acc.push(`${pet.displayName} ${lastName}`.trim())
    return acc
  }, [] as string[])
}

export const getOrderPrimaryServiceTypeName = (order?: Types.OrderAdminDto) =>
  order?.primaryServiceType?.serviceType?.name

// Gets the primary invoice that matchs the primary service type of the order
// On some cases (always?) the first invoice in the order is not the right one,
// for example TemperTests are PRIMARY, is on first place but is not the
// order's primary service type
export const getPrimaryInvoice = (order?: Types.OrderAdminDto) => {
  const primaryStId = order?.primaryServiceType?.serviceType?.id || ''
  return order?.invoices?.find(
    (invoice) =>
      (invoice.items?.findIndex(
        (item) =>
          item.offerType === 'PRIMARY' &&
          (item.locationServiceType?.serviceTypeId === primaryStId ||
            item.locationServiceType?.serviceType?.id === primaryStId)
      ) ?? -1) > -1
  )
}

export const isOvernightFromOrder = (order?: Types.OrderAdminDto) => !!order?.primaryServiceType?.serviceType?.overnight

export const isVisitOrder = (order?: Types.OrderAdminDto | Types.OrderClientDto) =>
  !!order?.primaryServiceType?.serviceType?.isVisit

export const isAdyenEnabled = (order?: Types.OrderAdminDto) => order?.location?.payFacConfig?.type === 'ADYEN'

export const isNewReservationOrder = (order?: Types.OrderAdminDto | Types.CustomOrderAdminDto): boolean =>
  order?.status === 'PENDING'

export const getOrderStatus = ({
  invoice,
  order,
  overnight,
}: {
  invoice?: Types.InvoiceAdminDto
  order?: Types.OrderAdminDto
  overnight?: boolean
}) => {
  const getStatus = (status?: string) => {
    switch (status) {
      case 'CANCELED':
        return t('global.label.canceled')
      case 'PARTIALLY_CANCELED':
        return t('global.label.partiallyCanceled')
      default:
        return null
    }
  }

  if (overnight === undefined) {
    return invoice ? getStatus(invoice?.invoiceStatus) : getStatus(order?.orderStatus)
  } else if (overnight) {
    return getStatus(invoice?.order?.orderStatus)
  } else {
    return getStatus(invoice?.invoiceStatus)
  }
}

export const getPrimaryInvoices = (
  order: Types.OrderAdminDto | Types.CustomOrderAdminDto
): Types.InvoiceAdminDto[] | Types.CustomInvoiceAdminDto[] => {
  return (
    order.invoices?.filter(
      (invoice) => !invoice.isInStay && invoice.items?.some((item) => item.offerType === 'PRIMARY')
    ) || []
  )
}
